import { motion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { CachedImage } from '@common/ui/CachedImage';

import { useHeartBeat, usePrefersReducedMotion } from '../hooks';
import styles from './SuperlikeButton.module.scss';

export type SuperlikeButtonProps = {
  /** Superlikes left today; undefined while the count is not known. */
  remaining: number | undefined;
  /** Beats now and then: the card is on top, idle, and nothing is open. */
  canBeat: boolean;
  /** Pressed in while its sheet is open and as the heart leaves it. */
  isPressed: boolean;
  /** On the photo (board 1.1) or in the feed's button row (board A.3). */
  variant?: 'photo' | 'row';
  onClick: () => void;
};

const SPRING_EASE = [0.3, 1.6, 0.5, 1] as const;

/**
 * The pink 3D heart with today's remaining count: as glass with a sparkle at
 * the bottom right of the photo (board 1.1), or as the row's biggest button
 * (board A.3). Greyed, without the count, when none are left.
 */
export const SuperlikeButton = ({
  remaining,
  canBeat,
  isPressed,
  variant = 'photo',
  onClick,
}: SuperlikeButtonProps) => {
  const isReducedMotion = usePrefersReducedMotion();
  const isSpent = remaining === 0;
  const isBeating = useHeartBeat(canBeat && !isSpent && !isReducedMotion);
  const ref = useRef<HTMLButtonElement>(null);

  // The card's drag listens to pointerdown natively on the card, before
  // React's root listener: a press that starts on the heart must not reach
  // it, or a press-and-slide here would end as a like or a pass.
  useEffect(() => {
    const button = ref.current;

    if (!button) return;

    const stop = (event: Event) => event.stopPropagation();

    button.addEventListener('pointerdown', stop);
    button.addEventListener('touchstart', stop, { passive: true });

    return () => {
      button.removeEventListener('pointerdown', stop);
      button.removeEventListener('touchstart', stop);
    };
  }, []);

  return (
    <motion.button
      ref={ref}
      type="button"
      className={styles.superlikeButton}
      data-test-id="superlike-button"
      data-variant={variant}
      data-spent={isSpent}
      data-pressed={isPressed}
      aria-label={
        isSpent
          ? 'Суперлайки закончились'
          : `Суперлайк, осталось ${remaining ?? ''}`.trim()
      }
      initial={false}
      animate={{ scale: isPressed ? 0.88 : 1 }}
      transition={{ duration: 0.3, ease: SPRING_EASE }}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <motion.span
        className={styles.heart}
        data-test-id="superlike-button-heart"
        data-beating={isBeating}
        initial={false}
        animate={{ scale: isBeating ? 1.16 : 1 }}
        transition={{ duration: 0.28, ease: SPRING_EASE }}
      >
        <CachedImage
          testId="superlike-button-icon"
          src="/icons/3d-icons/likes.png"
          alt=""
          blur="2px"
          className={styles.icon}
        />
      </motion.span>

      {!isSpent && variant === 'photo' && (
        <svg
          className={styles.sparkle}
          data-test-id="superlike-button-sparkle"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
        >
          <path
            d="M5 0 L6.1 3.9 L10 5 L6.1 6.1 L5 10 L3.9 6.1 L0 5 L3.9 3.9 Z"
            fill="currentColor"
          />
        </svg>
      )}

      {!isSpent && remaining !== undefined && (
        <span
          className={styles.counter}
          data-test-id="superlike-button-counter"
        >
          {remaining}
        </span>
      )}
    </motion.button>
  );
};
